/**
 * DogeOS SDK connect-kit adapter (DOGEOS_MYDOGE_JOINSPLIT_PLAN.md, phase 2).
 *
 * The DogeOS SDK exposes wallet connectors (MyDoge, WalletConnect, social and
 * embedded wallets) that are not EIP-6963 announcements. This module maps
 * each connector to the `EIP6963ProviderDetail` shape the existing wallet
 * modal and `connectWallet` already consume, so the Base wallet flow (chain
 * switch, EIP-712 derivation, D8 determinism check) runs unchanged on top of
 * them. Nothing from the kit is bundled: the kit is discovered at runtime on
 * the DogeOS route only, behind `REACT_APP_DOGEOS_CHIKYU_CONNECT_KIT=true`,
 * so Base bundles never carry it.
 *
 * Kit contract (what MyDoge V3 or the DogeOS SDK bootstrap installs on the
 * page): `window.DogeOSConnectKit = { connectors(): Promise<Connector[]> |
 * Connector[] }`, each connector `{ id, name, icon?, rdns?, getProvider():
 * Promise<EIP1193Provider> }`. Connectors whose `rdns` an EIP-6963 wallet
 * already announced are dropped: the injected wallet wins, never a duplicate.
 */
import type {
  EIP1193Provider,
  EIP6963ProviderDetail,
} from "../../base-app/hooks/useEIP6963";

export interface DogeOsConnectKitConnector {
  /** Stable connector id inside the kit (e.g. "mydoge", "walletConnect"). */
  id: string;
  name: string;
  icon?: string;
  /** Reverse-DNS wallet identifier when the connector wraps an injected wallet. */
  rdns?: string;
  getProvider(): Promise<EIP1193Provider> | EIP1193Provider;
}

export interface DogeOsConnectKit {
  connectors():
    | Promise<readonly DogeOsConnectKitConnector[]>
    | readonly DogeOsConnectKitConnector[];
}

declare global {
  interface Window {
    DogeOSConnectKit?: DogeOsConnectKit;
  }
}

/** The kit's connectors are namespaced so they never collide with EIP-6963 uuids. */
export const DOGEOS_CONNECT_KIT_UUID_PREFIX = "dogeos-connect-kit:";

export function connectKitEnabled(
  env: Record<string, string | undefined> = process.env,
): boolean {
  return (
    String(env.REACT_APP_DOGEOS_CHIKYU_CONNECT_KIT || "")
      .trim()
      .toLowerCase() === "true"
  );
}

function isConnector(value: unknown): value is DogeOsConnectKitConnector {
  const candidate = value as DogeOsConnectKitConnector;
  return (
    !!candidate &&
    typeof candidate === "object" &&
    typeof candidate.id === "string" &&
    candidate.id.trim().length > 0 &&
    typeof candidate.name === "string" &&
    candidate.name.trim().length > 0 &&
    typeof candidate.getProvider === "function" &&
    (candidate.rdns === undefined || typeof candidate.rdns === "string") &&
    (candidate.icon === undefined || typeof candidate.icon === "string")
  );
}

function isProvider(value: unknown): value is EIP1193Provider {
  return (
    !!value &&
    typeof value === "object" &&
    typeof (value as EIP1193Provider).request === "function"
  );
}

/**
 * An EIP-1193 provider that resolves the kit connector's provider on first
 * use. The wallet modal and `connectWallet` only ever call `request`, `on`
 * and `removeListener`, all of which wait for (and memoize) the connector.
 */
export function lazyConnectorProvider(
  connector: DogeOsConnectKitConnector,
): EIP1193Provider {
  let resolved: Promise<EIP1193Provider> | null = null;
  const provider = () => {
    if (!resolved) {
      resolved = Promise.resolve()
        .then(() => connector.getProvider())
        .then((value) => {
          if (!isProvider(value)) {
            throw new Error(
              `DogeOS connector "${connector.id}" returned no EIP-1193 provider`,
            );
          }
          return value;
        })
        .catch((error) => {
          // A failed connection may be retried; do not memoize the failure.
          resolved = null;
          throw error;
        });
    }
    return resolved;
  };
  return {
    request: (args) => provider().then((p) => p.request(args)),
    on: (event, handler) => {
      void provider().then((p) => p.on?.(event, handler));
    },
    removeListener: (event, handler) => {
      void provider().then((p) => p.removeListener?.(event, handler));
    },
  };
}

/** Map one kit connector to the wallet-modal shape. */
export function connectorToProviderDetail(
  connector: DogeOsConnectKitConnector,
): EIP6963ProviderDetail {
  return {
    info: {
      uuid: `${DOGEOS_CONNECT_KIT_UUID_PREFIX}${connector.id}`,
      name: connector.name,
      icon: connector.icon ?? "",
      rdns:
        connector.rdns ?? `${DOGEOS_CONNECT_KIT_UUID_PREFIX}${connector.id}`,
    },
    provider: lazyConnectorProvider(connector),
  };
}

/**
 * Merge EIP-6963 announcements with kit connectors: announced wallets first,
 * then kit connectors whose `rdns` (or id) no announcement already covers.
 * Malformed connectors are ignored rather than shown.
 */
export function mergeWalletChoices(
  announced: readonly EIP6963ProviderDetail[],
  connectors: readonly unknown[],
): EIP6963ProviderDetail[] {
  const seen = new Set<string>();
  const merged: EIP6963ProviderDetail[] = [];
  for (const detail of announced) {
    merged.push(detail);
    seen.add(detail.info.uuid);
    if (detail.info.rdns) seen.add(detail.info.rdns.toLowerCase());
  }
  for (const candidate of connectors) {
    if (!isConnector(candidate)) continue;
    const detail = connectorToProviderDetail(candidate);
    const keys = [detail.info.uuid, detail.info.rdns.toLowerCase()];
    if (keys.some((key) => seen.has(key))) continue;
    keys.forEach((key) => seen.add(key));
    merged.push(detail);
  }
  return merged;
}

/**
 * The connectors the page's kit offers, or none: the flag is off, the page
 * is not the DogeOS route, no kit was installed, or the kit failed. A kit
 * failure never breaks the wallet modal, which still lists EIP-6963 wallets.
 */
export async function loadDogeOsConnectKitConnectors(options: {
  enabled: boolean;
  kit?: DogeOsConnectKit | undefined;
}): Promise<DogeOsConnectKitConnector[]> {
  if (!options.enabled) return [];
  const kit =
    options.kit ??
    (typeof window === "undefined" ? undefined : window.DogeOSConnectKit);
  if (!kit || typeof kit.connectors !== "function") return [];
  try {
    const connectors = await kit.connectors();
    return Array.isArray(connectors) ? connectors.filter(isConnector) : [];
  } catch {
    return [];
  }
}
